import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { AuthProvider } from './Context/AuthContext';
import { BrowserRouter } from 'react-router-dom';
import { NavigationProvider } from './Context/NavigationContext';
import { DataProvider } from './Context/DataContext';
import { ClaimsProvider } from './Context/ClaimsContext';
import { FollowupProvider } from './Context/FollowupContext';
import { IntakeProvider } from './Context/IntakeContext';
import { HistoricProvider } from './Context/HistoricContext';
import { ChatProvider } from './Context/ChatContext';
import { ThemeProvider } from './Context/ThemeContext';

// Cache management - prevent blank screens from cache overflow
const manageBrowserCache = () => {
  try {
    const APP_VERSION = '1.0.0';
    const VERSION_KEY = 'intellasure_app_version';
    const LAST_CACHE_CLEAR = 'last_cache_clear';
    const CACHE_CLEAR_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 days

    const storedVersion = localStorage.getItem(VERSION_KEY);
    const lastCacheClear = localStorage.getItem(LAST_CACHE_CLEAR);
    const now = Date.now();

    // Check if we need to clear cache
    const needsCacheClear =
      storedVersion !== APP_VERSION ||
      !lastCacheClear ||
      (now - parseInt(lastCacheClear)) > CACHE_CLEAR_INTERVAL;

    if (needsCacheClear) {
      console.log('Clearing browser cache to prevent overflow...');

      // Clear localStorage except for essential items
      const essentialKeys = ['activeTab', 'theme', 'intellasure_user_id', 'userTimeZone'];
      const itemsToKeep: { [key: string]: string | null } = {};
      essentialKeys.forEach(key => {
        itemsToKeep[key] = localStorage.getItem(key);
      });

      localStorage.clear();

      // Restore essential items
      Object.entries(itemsToKeep).forEach(([key, value]) => {
        if (value !== null) {
          localStorage.setItem(key, value);
        }
      });

      // Clear sessionStorage
      sessionStorage.clear();

      // Update version and timestamp
      localStorage.setItem(VERSION_KEY, APP_VERSION);
      localStorage.setItem(LAST_CACHE_CLEAR, now.toString());

      console.log('Cache cleared successfully');
    }

    // Monitor localStorage size and warn if getting too large
    const checkStorageSize = () => {
      let totalSize = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          totalSize += localStorage[key].length + key.length;
        }
      }

      // Warn if storage is over 80% of typical 10MB limit (in chars, ~5MB)
      if (totalSize > 4000000) {
        console.warn('localStorage is getting full. Consider clearing cache.');
        // Auto-clear if critical
        if (totalSize > 4800000) {
          console.log('Critical storage limit reached. Auto-clearing...');
          localStorage.setItem(LAST_CACHE_CLEAR, '0'); // Force clear on next load
          window.location.reload();
        }
      }
    };

    checkStorageSize();

    // Periodic storage check every 5 minutes
    setInterval(checkStorageSize, 5 * 60 * 1000);

  } catch (error) {
    console.error('Error managing cache:', error);
    // If cache management fails, try emergency clear
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.error('Emergency cache clear failed:', e);
    }
  }
};

// Run cache management before rendering
manageBrowserCache();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <NavigationProvider>
          <DataProvider>
            <ClaimsProvider>
              <FollowupProvider>
                <IntakeProvider>
                  <HistoricProvider>
                    <ChatProvider>
                      <App/>
                    </ChatProvider>
                  </HistoricProvider>
                </IntakeProvider>
              </FollowupProvider>
            </ClaimsProvider>
          </DataProvider>
        </NavigationProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
